import { Box, Button, CircularProgress, Typography, useTheme } from '@mui/material';
import { useGet } from 'hooks/use-get';
import { usePost } from 'hooks/use-post';
import { Copy } from 'lucide-react';
import React, { useEffect, useMemo, useRef } from 'react';
import { DefaultValues, useForm, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import _ from 'underscore';
import { normalizeName } from 'utils/list-sort';
import titleCase from 'utils/title-case';

import Autocomplete from 'components/autocomplete';
import CheckboxSelectItem from 'components/checkbox-select-item';
import Dialog from 'components/dialog';
import RadioSelectItem from 'components/radio-select-item';
import Select from 'components/select';
import TextField from 'components/text-field';
import { useAccountState } from 'contexts/account/account-context';

import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { ScenarioModel } from 'types/shared/scenario-model-type.enum';
import { useBluViuNutritionState } from 'views/bluviu-nutrition/context/bluviu-nutrition-context';
import { getGenerations } from 'views/bluviu-nutrition/features/scenarios-v2/api/generations-get';
import { cloneScenario } from 'views/bluviu-nutrition/features/scenarios-v2/api/scenario-create';
import { useR2ScenarioState } from 'views/bluviu-nutrition/features/scenarios-v2/context/r2-scenario-context';
import { ScenarioCloneInput } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-clone-input.type';
import { ScenarioFeedProgram } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-feed-program.type';
import { ScenarioGeneration } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-generation.type';
import { Scenario } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario.type';
import { BluViuNutritionSite } from 'views/bluviu-nutrition/types/bluviu-nutrition-site.type';

type CloneableScenario = {
  id: string;
  name: string;
  globalSiteId: string;
  generation: ScenarioGeneration;
  feedProgram?: ScenarioFeedProgram[];
};

type Props = {
  scenario: CloneableScenario;
  button?: React.ReactElement;
  selectedFeedProgramIds?: string[];
};

interface CloneScenarioForm {
  scenarioGeneration: ScenarioGeneration;
  targetSite: BluViuNutritionSite;
  name: string;
  selectedFeedPrograms: string[];
}

const ScenarioCloneDialog: React.FC<Props> = ({ scenario, button, selectedFeedProgramIds }) => {
  const intl = useIntl();
  const theme = useTheme();
  const history = useHistory();
  const snackbarDispatch = useSnackbarDispatch();
  const {
    account: { company },
  } = useAccountState();
  const {
    r2ScenariosQuery: { data: scenarios },
  } = useR2ScenarioState();
  const {
    bluViuNutritionSitesQuery: { data: sites, isLoading },
  } = useBluViuNutritionState();

  // #region FETCH
  const { data: generations, isLoading: generationsIsLoading } = useGet(['getGenerations'], () => getGenerations());
  // #endregion

  const dialogRef = useRef<React.ElementRef<typeof Dialog>>(null);

  const scenarioNames = useMemo(() => scenarios?.map((scenario) => scenario.name), [scenarios]);

  const cloneScenarioPost = usePost<Scenario, { globalCompanyId: string; scenarioId: string; data: ScenarioCloneInput }>(
    ({ globalCompanyId, scenarioId, data }) => cloneScenario(globalCompanyId, scenarioId, data),
    (clonedScenario) => {
      dialogRef.current?.close();
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });

      history.push(`/bluviu-nutrition/r2/${clonedScenario.globalSiteId}/${clonedScenario.id}/setup`);
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  const feedPrograms = scenario.feedProgram;

  const defaultSelectedFeedPrograms = useMemo(
    () =>
      selectedFeedProgramIds?.length
        ? (feedPrograms
            ?.filter((fp) => selectedFeedProgramIds.includes(fp.id) || selectedFeedProgramIds.includes(fp.name))
            .map((fp) => fp.id) ?? [])
        : (feedPrograms?.map((fp) => fp.id) ?? []),
    [feedPrograms, selectedFeedProgramIds]
  );

  const currentScenarioSite = useMemo(
    () => sites?.find((site) => site.globalSiteId === scenario.globalSiteId),
    [sites, scenario.globalSiteId]
  );

  const defaultValues: DefaultValues<CloneScenarioForm> = useMemo(
    () => ({
      name: `${scenario.name} (Cloned)`,
      scenarioGeneration: scenario.generation,
      targetSite: currentScenarioSite,
      selectedFeedPrograms: defaultSelectedFeedPrograms,
    }),
    [scenario, currentScenarioSite, defaultSelectedFeedPrograms]
  );

  const { control, reset, handleSubmit, setValue } = useForm<CloneScenarioForm>({
    defaultValues,
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues]);

  const selectedFeedPrograms = useWatch({ control, name: 'selectedFeedPrograms' }) ?? [];
  const allFeedProgramIds = feedPrograms?.map((fp) => fp.id) ?? [];
  const allFeedProgramsSelected = !!allFeedProgramIds.length && allFeedProgramIds.every((id) => selectedFeedPrograms.includes(id));

  const onSubmit = (form: CloneScenarioForm) => {
    const data: ScenarioCloneInput = {
      name: form.name,
      generationSeason: form.scenarioGeneration.season,
      generationYear: form.scenarioGeneration.year,
      globalSiteId: form.targetSite.globalSiteId,
      includedFeedProgramIds: form.selectedFeedPrograms?.length ? form.selectedFeedPrograms : undefined,
    };

    cloneScenarioPost.mutate(
      { globalCompanyId: company!.companyId, scenarioId: scenario.id, data },
      {
        onSuccess: (clonedScenario) => {
          dialogRef.current?.close();
          history.push(`/bluviu-nutrition/r2/${clonedScenario.globalSiteId}/${clonedScenario.id}/setup`);
        },
      }
    );
  };

  return (
    <Dialog
      ref={dialogRef}
      maxWidth={'sm'}
      title={`New scenario (Clone)`}
      onClose={() => reset()}
      button={
        button ?? (
          <Button
            variant="text"
            startIcon={<Copy size={20} />}
            sx={{
              padding: 0,
              color: theme.palette.darkBlue.main,
              justifyContent: 'flex-start',
            }}>
            Clone scenario
          </Button>
        )
      }>
      <Box display={'flex'} flexDirection={'column'} gap={4}>
        <Typography fontSize={14}>From site: {currentScenarioSite?.siteName}</Typography>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box display={'flex'} flexDirection={'column'} gap={4}>
            <Box display={'flex'} flexDirection={'row'} gap={4}>
              <TextField
                fullWidth
                dark
                controller={{
                  control,
                  name: 'name',
                  rules: {
                    required: true,
                    validate: (value) =>
                      !scenarioNames?.includes(value) ? true : `Name must not match an existing scenario within the selected site.`,
                  },
                }}
                label={'Scenario name'}
              />
              <Autocomplete
                dark
                label={'Generation'}
                disabled={generationsIsLoading}
                disableCloseOnSelect={false}
                options={generations ?? []}
                getOptionLabel={(option) => (option as ScenarioGeneration).displayName}
                controller={{
                  control,
                  name: 'scenarioGeneration',
                  rules: {
                    required: true,
                  },
                }}
              />
            </Box>
            {/* For display purposes to the user only. Hardcoded */}
            <Select
              dark
              fullWidth
              label={'Model'}
              disabled
              value={ScenarioModel.EFFECTIVE_ENERGY_GT_2}
              renderValue={() => titleCase(ScenarioModel.EFFECTIVE_ENERGY_GT_2)}>
              <RadioSelectItem value={ScenarioModel.EFFECTIVE_ENERGY_GT_2}>
                {titleCase(ScenarioModel.EFFECTIVE_ENERGY_GT_2)}
              </RadioSelectItem>
            </Select>
            {!!feedPrograms?.length && (
              <Select
                multiple
                dark
                fullWidth
                label={'Feed programs'}
                renderValue={() => `${selectedFeedPrograms.length} of ${feedPrograms.length}`}
                onChange={(e) => {
                  const values = e.target.value as string[];
                  setValue(
                    'selectedFeedPrograms',
                    values.includes('all') ? (allFeedProgramsSelected ? [] : allFeedProgramIds) : values
                  );
                }}
                controller={{
                  control,
                  name: 'selectedFeedPrograms',
                }}>
                <CheckboxSelectItem key={'all-feed-programs'} checked={allFeedProgramsSelected} value={'all'} divider>
                  Select all
                </CheckboxSelectItem>
                {_.sortBy(feedPrograms, 'name').map((program) => (
                  <CheckboxSelectItem key={program.id} checked={selectedFeedPrograms.includes(program.id)} value={program.id}>
                    {program.name}
                  </CheckboxSelectItem>
                ))}
              </Select>
            )}
            <Autocomplete
              dark
              fullWidth
              label={'Target site'}
              disableCloseOnSelect={false}
              loading={isLoading}
              options={_.sortBy(sites ?? [], (site) => normalizeName(site.siteName))}
              getOptionLabel={(option) => option.siteName}
              controller={{
                control,
                name: 'targetSite',
                rules: {
                  required: true,
                },
              }}
            />
          </Box>
          <Box display={'flex'} justifyContent={'end'} mt={5}>
            <Button
              type="submit"
              disabled={cloneScenarioPost.isPending}
              startIcon={cloneScenarioPost.isPending ? <CircularProgress color="inherit" size={18} /> : null}>
              Clone
            </Button>
          </Box>
        </form>
      </Box>
    </Dialog>
  );
};

export default React.memo(ScenarioCloneDialog);
